"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShoppingCartProcessor = exports.ShoppingCartErrorCodes = exports.ProductTypeEnum = void 0;
const mathjs_1 = require("mathjs");
const transaction_1 = require("../calculations/transaction");
var ProductTypeEnum;
(function (ProductTypeEnum) {
    ProductTypeEnum["TICKET"] = "ticket";
    ProductTypeEnum["CUSTOM"] = "custom";
    ProductTypeEnum["SESSION"] = "session";
    ProductTypeEnum["DONATION"] = "donation";
})(ProductTypeEnum || (exports.ProductTypeEnum = ProductTypeEnum = {}));
var ShoppingCartErrorCodes;
(function (ShoppingCartErrorCodes) {
    ShoppingCartErrorCodes["ProductDoesNotExist"] = "product_does_not_exist";
    ShoppingCartErrorCodes["SellingDatePassed"] = "selling_date_passed";
    ShoppingCartErrorCodes["SellingDateNotArrived"] = "selling_date_not_arrived";
    ShoppingCartErrorCodes["NegativeProductQuantity"] = "negative_product_quantity";
    ShoppingCartErrorCodes["DuplicateProductsInCartItems"] = "duplicate_products_in_cart_items";
    ShoppingCartErrorCodes["QuantityExceedsMaxPerBuyer"] = "quantity_exceeds_max_per_buyer";
    ShoppingCartErrorCodes["QuantityExceedsAvailableQuantity"] = "quantity_exceeds_available_quantity";
})(ShoppingCartErrorCodes || (exports.ShoppingCartErrorCodes = ShoppingCartErrorCodes = {}));
class ShoppingCartProcessor {
    event;
    taxes;
    canellationTerms;
    orders;
    products;
    constructor(event, taxes, canellationTerms, products, orders) {
        this.event = event;
        this.taxes = taxes;
        this.canellationTerms = canellationTerms;
        this.orders = orders;
        this.products = new Map(products.map((p) => [p.id, p]));
    }
    productExists(productId) {
        return this.products.has(productId);
    }
    getProduct(productId) {
        return this.products.get(productId);
    }
    validateItems(cartItems) {
        const ticketItems = cartItems.filter((ci) => this.products.get(ci.product_id)?.type ===
            ProductTypeEnum.TICKET);
        if (ticketItems.length === 0) {
            return true;
        }
        return ticketItems.some((ci) => {
            return ci.quantity > 0;
        });
    }
    computeProducts() {
        return this.computeCartItems()
            .filter((item) => {
            // when product is already in cart we will show it in shopping cart regardless of publish date.
            if (item.quantity > 0) {
                return true;
            }
            const p = this.getProduct(item.product_id);
            if (p?.start_selling_at &&
                new Date(p.start_selling_at).getTime() > Date.now()) {
                return false;
            }
            if (p?.stop_selling_at &&
                new Date(p.stop_selling_at).getTime() < Date.now()) {
                return false;
            }
            return true;
        })
            .map((item) => {
            const product = this.getProduct(item.product_id);
            let maxPerBuyer = product.max_per_buyer;
            if (product?.max_per_buyer) {
                maxPerBuyer = Math.max(product.max_per_buyer, item.quantity);
                if (typeof product.available_quantity !== 'undefined') {
                    maxPerBuyer = Math.min(product.available_quantity + item.quantity, maxPerBuyer);
                }
            }
            const now = Date.now();
            if ((product?.stop_selling_at &&
                new Date(product.stop_selling_at).getTime() < now) ||
                (product?.start_selling_at &&
                    new Date(product.start_selling_at).getTime() > now)) {
                maxPerBuyer = item.quantity;
            }
            return {
                product: { ...product, max_per_buyer: maxPerBuyer },
                cart_quantity: item.quantity,
            };
        });
    }
    computeCartItems() {
        const result = {};
        this.orders.forEach((order) => {
            order.transactions.forEach((txn) => {
                if (!this._isBalance(txn)) {
                    return;
                }
                txn.items.forEach((txnItem) => {
                    // cancellation items are redundant so their quantities will not be counted.
                    if (txnItem.product.type === 'cancellation' ||
                        !txnItem.product.id) {
                        return;
                    }
                    // if product does not exist any more it's transactions must be ignored.
                    if (!this.productExists(txnItem.product.id)) {
                        return;
                    }
                    if (!result[txnItem.product.id]) {
                        result[txnItem.product.id] = {
                            quantity: 0,
                        };
                    }
                    result[txnItem.product.id].quantity += txnItem.quantity;
                });
            });
        });
        this.products.forEach((p, _) => {
            if (!result[p.id]) {
                result[p.id] = {
                    quantity: 0,
                };
            }
        });
        return Object.keys(result).map((pid) => ({
            product_id: pid,
            quantity: result[pid].quantity,
        }));
    }
    cancellOrders(orderIds) {
        const cancellableTxnItems = {};
        // get all refundable items for concerned orders.
        this.orders
            .filter((order) => orderIds.includes(order.id))
            .forEach((order) => {
            order.transactions.forEach((txn) => {
                if (!this._isBalance(txn)) {
                    return;
                }
                txn.items.forEach((txnItem) => {
                    if (txnItem.product.type === 'cancellation' ||
                        !txnItem.product.id ||
                        !this.productExists(txnItem.product.id)) {
                        return;
                    }
                    cancellableTxnItems[txnItem.id] = {
                        product_id: txnItem.product.id,
                        quantity: 0,
                    };
                });
            });
        });
        // calculate remaining quantities for concerned transactions items.
        this.orders.forEach((order) => {
            order.transactions.forEach((txn) => {
                if (!this._isBalance(txn)) {
                    return;
                }
                txn.items.forEach((txnItem) => {
                    if (cancellableTxnItems[txnItem.id]) {
                        cancellableTxnItems[txnItem.id].quantity +=
                            txnItem.quantity;
                    }
                    else if (txnItem.cancellation_item_id &&
                        cancellableTxnItems[txnItem.cancellation_item_id]) {
                        cancellableTxnItems[txnItem.cancellation_item_id].quantity += txnItem.quantity;
                    }
                });
            });
        });
        const cancellableProducts = {};
        Object.values(cancellableTxnItems).forEach((item) => {
            if (!cancellableProducts[item.product_id]) {
                cancellableProducts[item.product_id] = 0;
            }
            cancellableProducts[item.product_id] += item.quantity;
        });
        // remove quantities of cancellable orders from all orders.
        return this.computeCartItems().map((ci) => {
            if (cancellableProducts[ci.product_id]) {
                ci.quantity -= cancellableProducts[ci.product_id];
            }
            return ci;
        });
    }
    _getDiscountPerItem(item) {
        if (typeof item.discount === 'number' &&
            item.discount !== 0 &&
            item.quantity > 0) {
            return (0, mathjs_1.round)(item.discount / item.quantity, 2);
        }
        return 0;
    }
    _addCancellationCostPerItem(item, skipFee) {
        if (item.cancellation_item_id) {
            return item;
        }
        if (item.quantity <= 0) {
            return item;
        }
        if (skipFee) {
            item.cancellation_cost_per_item = 0;
            return item;
        }
        const secondsLeft = (this.event.start.getTime() - new Date().getTime()) / 1000;
        const effectivePrice = item.product.price + this._getDiscountPerItem(item);
        if (secondsLeft < 0) {
            item.cancellation_cost_per_item = effectivePrice;
            return item;
        }
        let costPercentage = 0;
        if (item.cancellation_terms?.terms?.length) {
            const sortedTerms = item.cancellation_terms.terms.sort((a, b) => b.from - a.from);
            for (const term of sortedTerms) {
                if (term.from < secondsLeft) {
                    break;
                }
                costPercentage = term.percentage;
            }
        }
        item.cancellation_cost_per_item = (0, mathjs_1.round)((effectivePrice * costPercentage) / 100, 2);
        return item;
    }
    process(input) {
        // stores overall quantities of the product id(s);.
        const quantities = new Map();
        // stores cancelled quantity for the transaction item id(s).
        const cancelledQuantities = new Map();
        // stores all the existing transactions.
        const existingTransactions = [];
        // iterate over all existing orders.
        this.orders.forEach((order) => {
            // iterate over all transactions in a order.
            order.transactions.forEach((txn) => {
                const isBalance = this._isBalance(txn);
                // quantities only matter if it's a balance transaction.
                // if it's not a balance transaction, return.
                if (!isBalance) {
                    return;
                }
                txn.items.forEach((txnItem) => {
                    // add cancellation cost per item to transaction item.
                    this._addCancellationCostPerItem(txnItem, input.skip_cancellation_fee ?? false);
                    // cancellation items are redundant so their quantities will not be counted.
                    if (txnItem.product.type === 'cancellation' ||
                        !txnItem.product.id) {
                        return;
                    }
                    quantities.set(txnItem.product.id, (quantities.get(txnItem.product.id) ?? 0) +
                        txnItem.quantity);
                    // add cancellation quantity.
                    if (txnItem.cancellation_item_id) {
                        cancelledQuantities.set(txnItem.cancellation_item_id, Math.abs(txnItem.quantity) +
                            (cancelledQuantities.get(txnItem.cancellation_item_id) ?? 0));
                    }
                });
                // add transaction to existing transactions.
                existingTransactions.push(txn);
            });
        });
        const errors = [];
        // stores transaction items for the current order transaction.
        const transactionsItems = [];
        // cancel products that:
        // - participant holds
        // - and are not in cart items
        // - and exists in products list.
        const finalCartItems = [...input.cartItems];
        quantities.forEach((_, productId) => {
            const isNotInCart = !finalCartItems.find((ci) => ci.product_id === productId);
            // Only cancel if it exists in products list but not in cart items.
            if (isNotInCart && this.productExists(productId)) {
                finalCartItems.push({
                    product_id: productId,
                    quantity: 0,
                });
            }
        });
        const seenInCartItems = new Set();
        for (const item of finalCartItems) {
            const productName = this.products.get(item.product_id)?.name || '';
            // negative quantities are not allowed.
            if (item.quantity < 0) {
                errors.push({
                    code: ShoppingCartErrorCodes.NegativeProductQuantity,
                    message: `Negative quantity for ${productName}`,
                });
                continue;
            }
            // If a product id is provided more than once in cart items.
            if (seenInCartItems.has(item.product_id)) {
                errors.push({
                    code: ShoppingCartErrorCodes.DuplicateProductsInCartItems,
                    message: `Product id (${item.product_id}) is provided more than once in cart items.`,
                });
                continue;
            }
            else {
                seenInCartItems.add(item.product_id);
            }
            const existingQuantity = quantities.get(item.product_id) ?? 0;
            let changeInQuantity = item.quantity - existingQuantity;
            if (changeInQuantity > 0) {
                const product = this.products.get(item.product_id);
                if (!product) {
                    errors.push({
                        code: ShoppingCartErrorCodes.ProductDoesNotExist,
                        message: `Product with id not found: ${item.product_id}`,
                    });
                    continue;
                }
                const now = new Date().getTime();
                if (product.start_selling_at &&
                    new Date(product.start_selling_at).getTime() > now) {
                    errors.push({
                        code: ShoppingCartErrorCodes.SellingDateNotArrived,
                        message: `${product.name} cannot be bought`,
                    });
                    continue;
                }
                if (product.stop_selling_at &&
                    new Date(product.stop_selling_at).getTime() < now) {
                    errors.push({
                        code: ShoppingCartErrorCodes.SellingDatePassed,
                        message: `${product.name} cannot be bought anymore`,
                    });
                    continue;
                }
                if (product &&
                    product.max_per_buyer &&
                    item.quantity > product?.max_per_buyer) {
                    errors.push({
                        // eslint-disable-next-line max-len
                        code: ShoppingCartErrorCodes.QuantityExceedsMaxPerBuyer,
                        message: `You can only buy ${product.max_per_buyer} units of ${productName}.`,
                    });
                    changeInQuantity = product.max_per_buyer - existingQuantity;
                    if (!changeInQuantity) {
                        continue;
                    }
                    if (changeInQuantity < 0) {
                        const { error, cancelledItems } = this._processNegativeChangeInQuantity(changeInQuantity, existingTransactions, item, cancelledQuantities);
                        if (error) {
                            errors.push({
                                code: error,
                                message: `Negative quantity for ${productName}`,
                            });
                            continue;
                        }
                        transactionsItems.push(...cancelledItems);
                    }
                }
                if (typeof product.available_quantity !== 'undefined' &&
                    changeInQuantity > product.available_quantity) {
                    errors.push({
                        // eslint-disable-next-line max-len
                        code: ShoppingCartErrorCodes.QuantityExceedsAvailableQuantity,
                        message: `Cannot buy ${productName} anymore; Only ${product.available_quantity} units are left.`,
                    });
                    changeInQuantity = product.available_quantity;
                    if (!changeInQuantity) {
                        continue;
                    }
                }
                // add +ve transaction for a purchase.
                const tItem = {
                    product: product,
                    tax: this._getTax(product),
                    cancellation_terms: this._getCancellationTerms(product),
                    quantity: changeInQuantity,
                };
                transactionsItems.push((0, transaction_1.calculateTransactionItem)(tItem, input.discount));
            }
            else if (changeInQuantity < 0) {
                const { error, cancelledItems } = this._processNegativeChangeInQuantity(changeInQuantity, existingTransactions, item, cancelledQuantities);
                if (error) {
                    errors.push({
                        code: error,
                        message: `Negative quantity for ${productName}`,
                    });
                    continue;
                }
                transactionsItems.push(...cancelledItems);
            }
        }
        const existingTransactionItems = existingTransactions.reduce((r, txn) => {
            r.push(...txn.items);
            return r;
        }, []);
        const oldTicketTotal = this._getTicketTotal([existingTransactionItems]);
        const newTicketTotal = this._getTicketTotal([
            existingTransactionItems,
            transactionsItems,
        ]);
        let finalTransactionItems = [];
        if (newTicketTotal >= oldTicketTotal) {
            finalTransactionItems = transactionsItems.filter((item) => {
                if (!item.cancellation_item_id ||
                    item.product?.type !== 'cancellation') {
                    return true;
                }
                const cancellationFor = existingTransactionItems.find((tofind) => tofind.id === item.cancellation_item_id);
                return cancellationFor?.product.type !== ProductTypeEnum.TICKET;
            });
        }
        else {
            finalTransactionItems = transactionsItems;
        }
        const transactionsToAdd = [];
        if (transactionsItems.length) {
            transactionsToAdd.push((0, transaction_1.calculateTransactionV2)(finalTransactionItems));
        }
        const donationProducts = [];
        for (const [product_id, product] of this.products) {
            if (product.type === ProductTypeEnum.DONATION) {
                donationProducts.push({
                    product_id,
                    price: product.price,
                });
            }
        }
        return {
            transactions: existingTransactions,
            transactionsToAdd: transactionsToAdd,
            errors: errors,
            donationProducts,
        };
    }
    setDonationPrices(donationProducts) {
        donationProducts.forEach((donationProduct) => {
            if (!this.products.has(donationProduct.product_id)) {
                return;
            }
            const product = this.products.get(donationProduct.product_id);
            if (product?.type !== ProductTypeEnum.DONATION) {
                return;
            }
            this.products.set(donationProduct.product_id, {
                ...product,
                price: donationProduct.price,
            });
        });
    }
    _getTicketTotal(itemslist) {
        let total = 0;
        for (const items of itemslist) {
            for (const txnItem of items) {
                if (txnItem.product.type === ProductTypeEnum.TICKET) {
                    total += txnItem.total;
                }
            }
        }
        return total;
    }
    _cancelFromTransactionItem(cancellationItem, quantityToCancel) {
        const items = [];
        const basePrice = cancellationItem.product.price;
        let discountedPrice = basePrice;
        if (cancellationItem.discount && cancellationItem.quantity) {
            discountedPrice =
                basePrice +
                    cancellationItem.discount / cancellationItem.quantity;
        }
        const tItem = {
            product: {
                ...cancellationItem.product,
                price: discountedPrice,
            },
            tax: cancellationItem.tax,
            discount: 0,
            cancellation_terms: cancellationItem.cancellation_terms,
            quantity: -quantityToCancel,
            cancellation_item_id: cancellationItem.id,
        };
        if (cancellationItem.reservation_id) {
            tItem.reservation_id = cancellationItem.reservation_id;
        }
        items.push((0, transaction_1.calculateTransactionItem)(tItem, null));
        if (cancellationItem.cancellation_cost_per_item !== 0) {
            const cIItem = {
                product: {
                    id: null,
                    name: 'Cancellation',
                    type: 'cancellation',
                    category: 'cancellation',
                    price: Math.abs(cancellationItem.cancellation_cost_per_item ?? 0),
                    currency: cancellationItem.product.currency,
                    more_than_one: false,
                    max_per_buyer: null,
                    tax_id: cancellationItem.product.tax_id,
                    cancellation_terms_id: cancellationItem.product.cancellation_terms_id,
                },
                discount: 0,
                tax: cancellationItem.tax,
                cancellation_terms: cancellationItem.cancellation_terms,
                quantity: quantityToCancel,
                cancellation_item_id: cancellationItem.id,
            };
            items.push((0, transaction_1.calculateTransactionItem)(cIItem, null));
        }
        return items;
    }
    _getCancellationTerms(product) {
        return product?.cancellation_terms_id
            ? this.canellationTerms.find((cTerms) => cTerms.id === product.cancellation_terms_id)
            : undefined;
    }
    _getTax(product) {
        return product?.tax_id
            ? this.taxes.find((ttax) => ttax.id === product.tax_id)
            : undefined;
    }
    _getCancellableItems(existingTransactions, item) {
        const selectItem = (txnItem) => {
            if (txnItem.product.id !== item.product_id) {
                return false;
            }
            if (txnItem.quantity <= 0) {
                return false;
            }
            if (txnItem.cancellation_item_id) {
                return false;
            }
            if (txnItem.product.type === 'cancellation') {
                return false;
            }
            return true;
        };
        return existingTransactions
            .flatMap((txn) => {
            if (this._isBalance(txn)) {
                return txn.items.filter((txnItem) => selectItem(txnItem));
            }
            return [];
        })
            .sort((a, b) => {
            const aCostPerItem = a.cancellation_cost_per_item;
            const bCostPerItem = b.cancellation_cost_per_item;
            if (typeof aCostPerItem === 'number' &&
                typeof bCostPerItem === 'number') {
                if (aCostPerItem === bCostPerItem) {
                    return a.quantity < b.quantity ? -1 : 1;
                }
                return aCostPerItem < bCostPerItem ? -1 : 1;
            }
            return -1;
        });
    }
    _isBalance(txn) {
        return transaction_1.BALANCE_TRANSACTION_STATUSES.includes(txn.status);
    }
    _processNegativeChangeInQuantity(changeInQuantity, existingTransactions, item, cancelledQuantities) {
        const toCancel = Math.abs(changeInQuantity);
        const cancellableItems = this._getCancellableItems(existingTransactions, item);
        let totalCancelled = 0;
        const localCancelledItems = [];
        for (const currentItem of cancellableItems) {
            if (toCancel === totalCancelled) {
                break;
            }
            // quantity that can be cancelled from current item.
            const canCancelFromItem = currentItem.quantity -
                (cancelledQuantities.get(currentItem.id) ?? 0);
            const toCancelFromItem = Math.min(canCancelFromItem, toCancel - totalCancelled);
            if (toCancelFromItem <= 0) {
                continue;
            }
            const txnItems = this._cancelFromTransactionItem(currentItem, toCancelFromItem);
            localCancelledItems.push(txnItems[0]);
            if (txnItems[1]) {
                localCancelledItems.push(txnItems[1]);
            }
            totalCancelled += toCancelFromItem;
        }
        // provided quantity cannot be cancelled.
        if (totalCancelled < toCancel) {
            return {
                error: ShoppingCartErrorCodes.NegativeProductQuantity,
            };
        }
        return {
            cancelledItems: localCancelledItems,
        };
    }
}
exports.ShoppingCartProcessor = ShoppingCartProcessor;
